import { clampTo, extractImageFrames } from '../../tools';
import type { WavespeedModelConfig } from '../types';

export const minimaxConfigs: Record<string, WavespeedModelConfig> = {
    'hailuo-h3': {
        // https://wavespeed.ai/zh-CN/models/minimax/h3/text-to-video
        // https://wavespeed.ai/zh-CN/models/minimax/h3/image-to-video
        // https://wavespeed.ai/zh-CN/models/minimax/h3/reference-to-video
        apiPath: ({ frameMode }) => {
            if (frameMode === 'first' || frameMode === 'first_last') {
                return '/minimax/h3/image-to-video';
            } else if (frameMode === 'ref') {
                return '/minimax/h3/reference-to-video';
            } else {
                return '/minimax/h3/text-to-video';
            }
        },
        pollApiPath: '/predictions/${requestId}/result',
        enableTranslation: false,
        buildInput: (params) => {
            const { prompt, imagePrompt, videoUri, videoUris, audioUri, audioUris, aspectRatio, duration, quality, modelUi } = params;
            const frameMode = modelUi?.frameMode;
            const [firstImage, lastImage] = extractImageFrames(imagePrompt);
            const images = (imagePrompt ?? []).filter(Boolean).slice(0, 9);
            const videos = (
                (videoUris?.length ?? 0) > 0 ? videoUris! : videoUri ? [videoUri] : []
            ).filter(Boolean).slice(0, 3);
            const audios = (
                (audioUris?.length ?? 0) > 0 ? audioUris! : audioUri ? [audioUri] : []
            ).filter(Boolean).slice(0, 3);
            // 首帧模式
            if (frameMode === 'first') {
                return {
                    prompt: prompt,
                    resolution: clampTo((quality || '2K').toLowerCase(), ['768p', '2k'] as const, '2k'),
                    duration: clampTo(duration || 5, [4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15] as const, 5),
                    // 首帧 URL
                    image: firstImage,
                };
            // 首尾帧模式
            } else if (frameMode === 'first_last') {
                return {
                    prompt: prompt,
                    resolution: clampTo((quality || '2K').toLowerCase(), ['768p', '2k'] as const, '2k'),
                    duration: clampTo(duration || 5, [4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15] as const, 5),
                    // 首帧 URL
                    image: firstImage,
                    // 尾帧 URL
                    last_image: lastImage,
                };
            // 参考模式
            } else if (frameMode === 'ref') {
                return {
                    prompt: prompt,
                    aspect_ratio: clampTo(aspectRatio || '16:9', ['16:9', '9:16', '1:1', '4:3', '3:4', '21:9'] as const, '16:9'),
                    duration: clampTo(duration || 5, [4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15] as const, 5),
                    resolution: clampTo((quality || '2K').toLowerCase(), ['768p', '2k'] as const, '2k'),
                    // 参考图片 URL
                    reference_images: images,
                    // 参考视频 URL
                    reference_videos: videos,
                    // 参考音频 URL
                    reference_audios: audios,
                };
            // 文生视频模式
            } else {
                return {
                    prompt: prompt,
                    aspect_ratio: clampTo(aspectRatio || '16:9', ['16:9', '9:16', '1:1', '4:3', '3:4', '21:9'] as const, '16:9'),
                    duration: clampTo(duration || 5, [4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15] as const, 5),
                    resolution: clampTo((quality || '2K').toLowerCase(), ['768p', '2k'] as const, '2k'),
                };
            }
        },
    },
    'minimax-h3-open': {
        // https://wavespeed.ai/zh-CN/models/wavespeed-ai/minimax-h3/text-to-video
        // https://wavespeed.ai/zh-CN/models/wavespeed-ai/minimax-h3/image-to-video
        // https://wavespeed.ai/zh-CN/models/wavespeed-ai/minimax-h3/reference-to-video
        apiPath: ({ frameMode }) => {
            if (frameMode === 'first' || frameMode === 'first_last') {
                return '/wavespeed-ai/minimax-h3/image-to-video';
            } else if (frameMode === 'ref') {
                return '/wavespeed-ai/minimax-h3/reference-to-video';
            } else {
                return '/wavespeed-ai/minimax-h3/text-to-video';
            }
            
        },
        pollApiPath: '/predictions/${requestId}/result',
        enableTranslation: false,
        buildInput: (params) => {
            const { prompt, imagePrompt, videoUri, videoUris, audioUri, audioUris, aspectRatio, duration, quality, modelUi, providerOptions } = params;
            const frameMode = modelUi?.frameMode;
            const [firstImage, lastImage] = extractImageFrames(imagePrompt);
            const images = (imagePrompt ?? []).filter(Boolean).slice(0, 9);
            const videos = (
                (videoUris?.length ?? 0) > 0 ? videoUris! : videoUri ? [videoUri] : []
            ).filter(Boolean).slice(0, 3);
            const audios = (
                (audioUris?.length ?? 0) > 0 ? audioUris! : audioUri ? [audioUri] : []
            ).filter(Boolean).slice(0, 3);
            // 首帧模式
            if (frameMode === 'first') {
                return {
                    prompt: prompt,
                    duration: clampTo(duration || 5, [5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15] as const, 5),
                    resolution: clampTo(quality || '480p', ['480p', '768p'] as const, '480p'),
                    seed: typeof (providerOptions as any)?.seed === 'number' ? (providerOptions as any).seed : undefined,
                    // 首帧 URL
                    image: firstImage,
                };
            // 首尾帧模式
            } else if (frameMode === 'first_last') {
                return {
                    prompt: prompt,
                    resolution: clampTo(quality || '480p', ['480p', '768p'] as const, '480p'),
                    duration: clampTo(duration || 5, [5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15] as const, 5),
                    seed: typeof (providerOptions as any)?.seed === 'number' ? (providerOptions as any).seed : undefined,
                    // 首帧 URL
                    image: firstImage,
                    // 尾帧 URL
                    lastImage: lastImage,
                };
            // 参考模式
            } else if (frameMode === 'ref') {
                return {
                    prompt: prompt,
                    aspect_ratio: clampTo(aspectRatio || '16:9', ['16:9', '9:16', '1:1', '4:3', '3:4', '21:9', '9:21'] as const, '16:9'),
                    duration: clampTo(duration || 5, [5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15] as const, 5),
                    resolution: clampTo(quality || '480p', ['480p', '768p'] as const, '480p'),
                    seed: typeof (providerOptions as any)?.seed === 'number' ? (providerOptions as any).seed : undefined,
                    // 参考图片 URL
                    reference_images: images,
                    // 参考视频 URL
                    reference_videos: videos,
                    // 参考音频 URL
                    reference_audios: audios,
                };
            // 文生视频模式
            } else {
                return {
                    prompt: prompt,
                    aspect_ratio: clampTo(aspectRatio || '16:9', ['16:9', '9:16', '1:1', '4:3', '3:4', '21:9', '9:21'] as const, '16:9'),
                    duration: clampTo(duration || 5, [5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15] as const, 5),
                    resolution: clampTo(quality || '480p', ['480p', '768p'] as const, '480p'),
                    seed: typeof (providerOptions as any)?.seed === 'number' ? (providerOptions as any).seed : undefined,
                };
            }
        },
    },
};